import React, { useMemo } from 'react';
import styles from '../../styles/AsignacionGruposStyles/AsignacionGruposExtended.module.css';

// Mapeo de nombre de día -> índice (0=lunes ... 6=domingo), tolerante a acentos.
const DIA_INDEX = {
  lunes: 0, martes: 1, miercoles: 2, 'miércoles': 2, jueves: 3,
  viernes: 4, sabado: 5, 'sábado': 5, domingo: 6,
};
const DAY_LABELS = ['LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO', 'DOMINGO'];

// Convierte 'HH:MM:SS' o 'HH:MM' en minutos desde medianoche + etiqueta legible.
function parseHora(str) {
  if (!str) return null;
  const parts = String(str).split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] || '0', 10);
  if (Number.isNaN(h)) return null;
  return { min: h * 60 + m, hour: h, label: `${h}:${String(m).padStart(2, '0')}` };
}

function dayIndexFrom(value) {
  if (typeof value === 'number') return value;
  return DIA_INDEX[String(value).toLowerCase()];
}

function ScheduleModal({
  student,
  gruposAsignados = [],
  gruposDisponibles = [],
  gruposSeleccionados = [],
  siguienteCiclo,
  onClose,
}) {
  const { sessions, warnings, periodoLabel } = useMemo(() => {
    const sessions = [];
    const warnings = [];

    // El horario que se previsualiza corresponde al CICLO SIGUIENTE. Los grupos
    // asignados (esc_estudiantes_grupos) pueden incluir ciclos anteriores (materias
    // ya aprobadas o en curso), por lo que se filtran al ciclo siguiente cuando se
    // conoce su id. Si no se pudo determinar, se muestran todos (mejor esfuerzo).
    const cicloSiguienteId = siguienteCiclo?.id_ciclo_escolar ?? null;

    // 1) Grupos ya asignados del ciclo siguiente (fuente: esc_estudiantes_grupos)
    (gruposAsignados || []).forEach((g) => {
      if (cicloSiguienteId != null && String(g.id_ciclo_escolar_grupo) !== String(cicloSiguienteId)) {
        return;
      }
      (g.horarios || []).forEach((h) => {
        sessions.push({
          dayIdx: dayIndexFrom(h.dia),
          ini: parseHora(h.hora_inicio),
          fin: parseHora(h.hora_fin),
          materia: g.materia_nombre || 'Materia',
          code: g.grupo_codigo || '',
          docente: g.asesor_nombre || '',
          aula: h.aula || '',
          origen: 'asignado',
          idMateria: g.id_materia,
        });
      });
    });

    // 2) Grupos seleccionados (aún sin guardar) tomados de los disponibles
    const selSet = new Set((gruposSeleccionados || []).map(String));
    (gruposDisponibles || []).forEach((g) => {
      if (!selSet.has(String(g.id))) return;
      const raw = g._raw || {};
      const horario = raw.horario || {};
      const dias = Array.isArray(horario.dias) ? horario.dias : [];
      const ini = parseHora(horario.hora_inicio);
      const fin = parseHora(horario.hora_fin);
      dias.forEach((d) => {
        sessions.push({
          dayIdx: dayIndexFrom(d),
          ini,
          fin,
          materia: g.materia || raw.materia_nombre || 'Materia',
          code: g.codigo_grupo || '',
          docente: g.asesor || raw.asesor_nombre || '',
          aula: raw.aula || '',
          origen: 'seleccionado',
          idMateria: g.id_materia,
        });
      });
    });

    // ── Validaciones de horario (C4) ──
    // a) Horas inválidas (faltantes o inicio >= fin)
    sessions.forEach((s) => {
      if (!s.ini || !s.fin || s.ini.min >= s.fin.min) {
        s.invalida = true;
        warnings.push({ msg: `Horario inválido en "${s.materia}"${s.code ? ` (${s.code})` : ''}.` });
      }
    });

    // b) Materia duplicada (misma materia con más de un grupo)
    const porMateria = {};
    sessions.forEach((s) => {
      if (s.idMateria == null) return;
      (porMateria[s.idMateria] = porMateria[s.idMateria] || new Set()).add(s.code || '—');
    });
    Object.entries(porMateria).forEach(([mid, codes]) => {
      if (codes.size > 1) {
        const nombre = sessions.find((s) => String(s.idMateria) === String(mid))?.materia;
        warnings.push({ msg: `La materia "${nombre}" tiene más de un grupo (${Array.from(codes).join(', ')}).` });
      }
    });

    // c) Traslapes (mismo día con rangos horarios que se cruzan)
    for (let i = 0; i < sessions.length; i++) {
      for (let j = i + 1; j < sessions.length; j++) {
        const a = sessions[i];
        const b = sessions[j];
        if (a.dayIdx !== b.dayIdx) continue;
        if (!a.ini || !a.fin || !b.ini || !b.fin) continue;
        if (a.ini.min < b.fin.min && b.ini.min < a.fin.min) {
          a.conflicto = true;
          b.conflicto = true;
          const dl = DAY_LABELS[a.dayIdx] || 'día';
          warnings.push({
            msg: `Traslape el ${dl}: "${a.materia}"${a.code ? ` (${a.code})` : ''} y "${b.materia}"${b.code ? ` (${b.code})` : ''}.`,
          });
        }
      }
    }

    // d) Sesiones en domingo (la cuadrícula muestra Lun-Sáb)
    if (sessions.some((s) => s.dayIdx > 5)) {
      warnings.push({ msg: 'Hay sesiones en domingo que no se muestran en la cuadrícula.' });
    }

    // Eliminar advertencias repetidas
    const seen = new Set();
    const uniqWarnings = warnings.filter((w) => {
      if (seen.has(w.msg)) return false;
      seen.add(w.msg);
      return true;
    });

    const periodoLabel = siguienteCiclo?.nombre_ciclo || student?.periodoActual || '—';
    return { sessions, warnings: uniqWarnings, periodoLabel };
  }, [gruposAsignados, gruposDisponibles, gruposSeleccionados, siguienteCiclo, student]);

  const timeSlots = [
    '8:00', '9:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00',
    '16:00', '17:00', '18:00', '19:00', '20:00', '21:00',
  ];
  const days = DAY_LABELS.slice(0, 6);

  const getSessionsForSlot = (dayIdx, slotHour) =>
    sessions.filter((s) => s.dayIdx === dayIdx && s.ini && s.ini.hour === slotHour);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.scheduleModal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <h3>Horario {periodoLabel !== '—' ? `(${periodoLabel})` : ''}</h3>
            <div className={styles.studentInfo}>
              <span><strong>Nombre:</strong> {student?.nombre || 'N/A'}</span>
              <span><strong>Período:</strong> {periodoLabel}</span>
              <span><strong>Matrícula:</strong> {student?.matricula || 'N/A'}</span>
              <span><strong>Programa:</strong> {student?.programa || 'N/A'}</span>
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {warnings.length > 0 && (
          <div
            style={{
              margin: '12px 20px 0',
              padding: '10px 14px',
              backgroundColor: '#fff4e5',
              border: '1px solid #ffb74d',
              borderRadius: 6,
              color: '#8a4b00',
              font: '400 12px Roboto, -apple-system, Helvetica, sans-serif',
            }}
          >
            <strong style={{ display: 'block', marginBottom: 4 }}>
              Se detectaron inconsistencias en el horario:
            </strong>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {warnings.map((w, i) => (
                <li key={i}>{w.msg}</li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.scheduleGrid}>
          <div className={styles.scheduleTable}>
            {/* Header */}
            <div className={styles.scheduleHeader}>
              <div className={styles.timeColumn}>Hora</div>
              {days.map((day) => (
                <div key={day} className={styles.dayColumn}>{day}</div>
              ))}
            </div>

            {/* Time slots */}
            {timeSlots.map((time) => {
              const slotHour = parseInt(time, 10);
              return (
                <div key={time} className={styles.timeRow}>
                  <div className={styles.timeCell}>{time}</div>
                  {days.map((day, dayIdx) => {
                    const cellSessions = getSessionsForSlot(dayIdx, slotHour);
                    const hasConflict = cellSessions.length > 1 || cellSessions.some((s) => s.conflicto || s.invalida);
                    return (
                      <div key={`${day}-${time}`} className={styles.scheduleCell}>
                        {cellSessions.map((s, idx) => (
                          <div
                            key={idx}
                            className={styles.subjectBlock}
                            style={{
                              backgroundColor: hasConflict ? '#c62828' : '#5f7fa2',
                              marginBottom: cellSessions.length > 1 && idx < cellSessions.length - 1 ? 3 : 0,
                            }}
                            title={`${s.materia}${s.code ? ` · ${s.code}` : ''}${s.docente ? ` · ${s.docente}` : ''}${s.aula ? ` · Aula ${s.aula}` : ''}${s.ini && s.fin ? ` · ${s.ini.label}-${s.fin.label}` : ''}`}
                          >
                            <div className={styles.subjectName}>{s.materia}</div>
                            <div className={styles.subjectCode}>
                              {s.code}
                              {s.ini && s.fin ? ` · ${s.ini.label}-${s.fin.label}` : ''}
                            </div>
                            {(s.docente || s.aula) && (
                              <div className={styles.subjectCode}>
                                {s.docente}{s.docente && s.aula ? ' · ' : ''}{s.aula ? `Aula ${s.aula}` : ''}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.modalActions}>
          <button className={styles.cancelModalButton} onClick={onClose}>
            CERRAR
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScheduleModal;
